import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Scissors, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface ClipCreatorProps {
  streamId: string;
  currentTime?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ClipCreator = ({ streamId, currentTime = 0, open, onOpenChange }: ClipCreatorProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState(Math.max(0, currentTime - 30));
  const [duration, setDuration] = useState(30);
  const [creating, setCreating] = useState(false);

  const handleCreateClip = async () => {
    if (!title.trim()) {
      toast.error('Please enter a title for the clip');
      return;
    }

    if (duration < 5 || duration > 60) {
      toast.error('Clip duration must be between 5 and 60 seconds');
      return;
    }

    try {
      setCreating(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please login to create clips');
        return;
      }

      // Get stream info
      const { data: stream } = await supabase
        .from('streams')
        .select('creator_id')
        .eq('id', streamId)
        .single();

      if (!stream) {
        toast.error('Stream not found');
        return;
      }

      // Create clip record
      const { error } = await supabase
        .from('recordings')
        .insert({
          stream_id: streamId,
          creator_id: stream.creator_id,
          title: title.trim(),
          description: description.trim(),
          duration,
          status: 'processing',
          is_published: true,
        });

      if (error) throw error;

      toast.success('Clip created! Processing may take a few moments.');
      onOpenChange(false);
      
      // Reset form
      setTitle('');
      setDescription('');
      setStartTime(Math.max(0, currentTime - 30));
      setDuration(30);
    } catch (error: any) {
      console.error('Error creating clip:', error);
      toast.error(error.message || 'Failed to create clip');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="h-5 w-5" />
            Create Clip
          </DialogTitle>
          <DialogDescription>
            Capture and share this moment from the live stream
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="clip-title">Title *</Label>
            <Input
              id="clip-title"
              placeholder="Epic moment..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="clip-description">Description</Label>
            <Textarea
              id="clip-description"
              placeholder="What makes this clip special?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start-time">Start Time (seconds)</Label>
              <Input
                id="start-time"
                type="number"
                min={0}
                value={startTime}
                onChange={(e) => setStartTime(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration">Duration (seconds)</Label>
              <Input
                id="duration"
                type="number"
                min={5}
                max={60}
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 30)}
              />
              <p className="text-xs text-muted-foreground">5-60 seconds</p>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-3 text-sm">
            <p className="text-muted-foreground">
              <strong>Preview:</strong> Clip will capture from {startTime}s to {startTime + duration}s
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreateClip} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Scissors className="h-4 w-4 mr-2" />
                Create Clip
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
